import { PlusOutlined } from '@ant-design/icons';
import { Divider, Button, type FormItemProps, Space, message } from 'antd';
import { FieldArray, useField } from 'formik';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { type ModuleSpecsFragment } from '../../../../api/fragments';
import { ConditionType, type ConditionSettings } from '../../../../api/types';
import FormItem from '../../../../components/fields/FormItem';
import { useAddConditionsDrawer } from './AddConditionsDrawer';
import ConditionContainer from './ConditionContainer';

const StyledButton = styled.div`
    text-align: center;
`;

export const isConditionSettingsComplete = (conditionSetting: ConditionSettings): boolean => {
    if (!conditionSetting.type) {
        return false;
    }

    switch (conditionSetting.type) {
        case ConditionType.And:
        case ConditionType.Or: {
            if (!conditionSetting.logicConditionSettings) {
                return false;
            }
            const { children } = conditionSetting.logicConditionSettings;

            // every child condition should be complete as well
            return !!conditionSetting.type && children.every(setting => isConditionSettingsComplete(setting));
        }

        case ConditionType.IsApplicationModule:
            return !!conditionSetting.type && !!conditionSetting.applicationModuleConditionSettings;

        case ConditionType.IsBank:
            return !!conditionSetting.type && !!conditionSetting.bankConditionSettings;

        case ConditionType.IsDealer:
            return !!conditionSetting.type && !!conditionSetting.dealerConditionsSettings;

        case ConditionType.IsInsurer:
            return !!conditionSetting.insurerConditionSettings;

        case ConditionType.IsLocation:
            return !!conditionSetting.type && !!conditionSetting.locationConditionSettings;

        case ConditionType.IsGiftVoucher:
            return !!conditionSetting.type && !!conditionSetting.giftVoucherConditionSettings;

        default:
            return !!conditionSetting.type;
    }
};

const renderConditionContainer = (onClick: () => void, onDelete: () => void, conditionSettings, index: number) => {
    switch (conditionSettings.type) {
        case ConditionType.Or:
        case ConditionType.And:
            return (
                <ConditionContainer
                    conditionSettings={conditionSettings}
                    onClick={onClick}
                    onDelete={onDelete}
                    logicCondition
                />
            );

        default:
            return <ConditionContainer conditionSettings={conditionSettings} onClick={onClick} onDelete={onDelete} />;
    }
};

export type ConditionsDisplayProps = {
    name: string;
    label?: string;
    itemProps?: Omit<FormItemProps, 'label' | 'meta' | 'required' | 'children'>;
    module?: ModuleSpecsFragment;
    disabled?: boolean;
};

type ConditionComponentProps = {
    name: string;
    index: number;
    onDelete: () => void;
    value;
    disabled: boolean;
    isLast: boolean;
};

const ConditionComponent = ({ name, index, onDelete, value, disabled, isLast }: ConditionComponentProps) => {
    const { t } = useTranslation('consentsAndDeclarations');
    const addConditionDrawer = useAddConditionsDrawer(name, index, value);

    const onClick = () => {
        addConditionDrawer.open(false);
    };

    return (
        <>
            {isConditionSettingsComplete(value[index]) &&
                renderConditionContainer(onClick, onDelete, value[index], index)}
            {isConditionSettingsComplete(value[index]) && !(isLast && disabled) && (
                <Divider>{t('consentsAndDeclarations:andDivider')}</Divider>
            )}
            {addConditionDrawer.render(onDelete)}
        </>
    );
};

const ConditionsDisplay = ({ name, label, itemProps, module, disabled }: ConditionsDisplayProps) => {
    const { t } = useTranslation('consentsAndDeclarations');
    const [field, meta] = useField({ name });
    const { value } = meta;

    const addConditionDrawer = useAddConditionsDrawer(name, value.length - 1, value, module);

    return (
        <FormItem {...itemProps} label={label} meta={meta} name={name}>
            <FieldArray
                {...field}
                name={name}
                render={arrayHelpers => {
                    const onClick = () => {
                        arrayHelpers.push('');
                        addConditionDrawer.open(true);
                    };

                    const onDelete = () => {
                        arrayHelpers.pop();
                    };

                    const addButton = !disabled ? (
                        <StyledButton>
                            <Button icon={<PlusOutlined />} onClick={onClick} type="link">
                                {t('consentsAndDeclarations:addCondition')}
                            </Button>
                            {addConditionDrawer.render(onDelete)}
                        </StyledButton>
                    ) : null;

                    return (
                        <Space direction="vertical" style={{ width: '100%' }}>
                            {value?.map((key: string, index: number) => {
                                // to remove specific conditions
                                const onDelete = () => {
                                    arrayHelpers.remove(index);
                                    message.success({
                                        content: t('consentsAndDeclarations:conditionsDrawer.messages.remove'),
                                        key: 'primary',
                                    });
                                };

                                return (
                                    <ConditionComponent
                                        // eslint-disable-next-line react/no-array-index-key
                                        key={`${name}-${index}`}
                                        disabled={disabled}
                                        index={index}
                                        isLast={value.length - 1 === index}
                                        name={name}
                                        onDelete={onDelete}
                                        value={value}
                                    />
                                );
                            })}
                            {addButton}
                        </Space>
                    );
                }}
            />
        </FormItem>
    );
};

export default ConditionsDisplay;
